import type { ProjectIdentity } from './project';
import type { GatewayProvider } from './payment';

export type TrainingCycle = 'monthly' | 'bimonthly';

export const TRAINING_CYCLE_LABELS: Record<TrainingCycle, string> = {
  monthly: 'Mensal',
  bimonthly: 'Bimestral',
};

export interface SubscriptionPlan {
  id: number;
  name: string;
  price_cents: number;
  billing_cycle: string;
  training_cycle: TrainingCycle;
  loyalty_months: number;
  auto_renew: boolean;
  is_single_payment: boolean;
  description: string | null;
  benefits: string | null;
  is_active: boolean;
  is_visible_in_app: boolean;
  created_at: string;
  project_id?: number | null;
  project?: ProjectIdentity | null;
}

export type SubscriptionOrigin = 'portal' | 'app' | 'checkout_screen';
export type StudentSubscriptionPaymentMethod = 'pix' | 'credit_card';
export type StudentSubscriptionEndedReason =
  | 'completed_term'
  | 'plan_transition'
  | 'manual_cancel'
  | 'checkout_failed';
export type StudentSubscriptionTransitionStatus = 'scheduled' | 'processing' | 'completed' | 'failed' | 'canceled';
export type SubscriptionPauseResumeReason =
  | 'scheduled_end'
  | 'manual_resume'
  | 'subscription_canceled'
  | 'plan_transition';

export interface ActiveSubscriptionPause {
  id: number;
  student_subscription_id: number;
  paused_from: string;
  paused_until: string;
  scheduled_resume_date: string;
  reason?: string | null;
}

export interface StudentSubscriptionPauseRecord {
  id: number;
  student_subscription_id: number;
  paused_from: string;
  paused_until: string;
  resumed_at: string | null;
  resume_reason: SubscriptionPauseResumeReason | null;
  reason: string | null;
  created_by_user_id: string | null;
  resumed_by_user_id: string | null;
  gateway_state: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface SubscriptionPauseState {
  isPausedToday: boolean;
  activePause: StudentSubscriptionPauseRecord | null;
  totalPausedDaysUpToToday: number;
  scheduledResumeDate: string | null;
}

export interface StudentSubscription {
  id: number;
  plan_id: number;
  plan_name: string;
  price_cents: number;
  billing_cycle: string;
  training_cycle: TrainingCycle;
  loyalty_months: number;
  auto_renew: boolean;
  is_single_payment: boolean;
  plan_description: string | null;
  benefits: string | null;
  discount_cents: number;
  canceled_at: string | null;
  ended_at: string | null;
  ended_reason: StudentSubscriptionEndedReason | null;
  cancellation_reason: string | null;
  cancellation_observation: string | null;
  canceled_by_user_id: string | null;
  replaced_by_subscription_id: number | null;
  external_subscription_id: string | null;
  payment_system?: string | null;
  has_paid_charge?: boolean | null;
  student_payment_account_id: number | null;
  start_date: string | null;
  origin: SubscriptionOrigin | null;
  /** Locked Asaas method for portal-created subscriptions (pix | credit_card). */
  payment_method: StudentSubscriptionPaymentMethod | null;
  created_by_portal_user_id: number | null;
  created_by_name?: string | null;
  created_at: string;
  project_id?: number | null;
  project?: ProjectIdentity | null;
  active_pause?: StudentSubscriptionPauseRecord | null;
}

export interface StudentSubscriptionTransition {
  id: number;
  current_student_subscription_id: number;
  next_plan_id: number;
  next_plan_name: string;
  next_price_cents: number;
  next_billing_cycle: string;
  next_loyalty_months: number;
  next_auto_renew: boolean;
  next_is_single_payment: boolean;
  next_plan_description: string | null;
  next_benefits: string | null;
  gateway_provider: GatewayProvider | 'pix';
  next_gateway_provider: GatewayProvider | 'pix';
  next_student_payment_account_id: number | null;
  effective_start_date: string;
  status: StudentSubscriptionTransitionStatus;
  next_student_subscription_id: number | null;
  processing_started_at: string | null;
  executed_at: string | null;
  failure_reason: string | null;
  created_by_user_id: string | null;
  created_at: string;
  updated_at: string;
}
